import { useWorkPeriodsMeasureMultiple } from '../../../api/work-periods-client/work-periods-client.hooks';
import {
  Measure,
  type MeasureDataByEntity,
  TimeAllocationType,
  Transformer,
  type UseWorkPeriodsMeasurePayloadMultiple,
} from '../../../api/work-periods-client/work-periods-client.type';
import { formatToISODate } from '../../../helpers/date-time-helpers';
import { useProcessAnalysisStore } from '../../../store/process-analysis-store/process-analysis-store';
import {
  useAvailableMeasureNames,
  useDateRange,
  useEntities,
  useTimeAllocation,
} from '../../../store/process-analysis-store/process-analysis-store.hooks';
import { getEntityType, splitMeasures } from '../../../helpers/process-analysis-shared/process-analysis-shared.helpers';
import { TRANSFORMER_MEASURES } from '../process-analysis.constants';

/**
 * Custom hook to fetch key measures data over time for all measures.
 *
 * This hook fetches multiple sets of measure data:
 * - Default measures split into 6 groups to optimize loading time
 * - Custom measures defined by the user, split into 4 groups to optimize loading time
 * - Measures that require special transformers
 *
 * The data from all queries is merged into a single MeasureDataByEntity object.
 *
 * @param {Object} [options] - Optional configuration
 * @param {TimeAllocationType} [options.overwriteTimeAllocation] - Optional time allocation type to override the default
 * @param {boolean} [options.enabled=true] - Whether the hook should fetch data
 * @returns {Object} An object containing:
 * @property {MeasureDataByEntity | undefined} data - The merged measure data from all queries
 * @property {boolean} isFetching - True if any of the queries are still fetching
 *   (initial load OR background refetch)
 * @property {boolean} isLoading - True only when at least one of the
 *   underlying queries is still on its first fetch with no cached data —
 *   use to drive skeleton placeholders without flashing on cached navigations
 */
const useKeyMeasuresOverTimeData = (options?: {
  overwriteTimeAllocation?: TimeAllocationType;
  enabled?: boolean;
}): { data: MeasureDataByEntity | undefined; isFetching: boolean; isLoading: boolean } => {
  const { overwriteTimeAllocation, enabled = true } = options ?? {};
  const measures = useAvailableMeasureNames();
  const customMeasures = useProcessAnalysisStore((state) => state.availableMeasures)
    .filter((measure) => measure.is_custom)
    .map((measure) => measure.measure_name);

  const groups = splitMeasures(measures, 6);
  const customGroups = splitMeasures(customMeasures, 4);

  const timeAllocation = useTimeAllocation();
  const timeAllocationToUse = overwriteTimeAllocation || timeAllocation;

  const {
    data: defaultDataFirst,
    isFetching: isFetchingDefaultDataFirst,
    isLoading: isLoadingDefaultDataFirst,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[0], timeAllocationToUse),
    options: {
      enabled: enabled && !!groups[0]?.length,
    },
  });

  const {
    data: defaultDataSecond,
    isFetching: isFetchingDefaultDataSecond,
    isLoading: isLoadingDefaultDataSecond,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[1], timeAllocationToUse),
    options: {
      enabled: enabled && !!groups[1]?.length,
    },
  });

  const {
    data: defaultDataThird,
    isFetching: isFetchingDefaultDataThird,
    isLoading: isLoadingDefaultDataThird,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[2], timeAllocationToUse),
    options: {
      enabled: enabled && !!groups[2]?.length,
    },
  });

  const {
    data: defaultDataFourth,
    isFetching: isFetchingDefaultDataFourth,
    isLoading: isLoadingDefaultDataFourth,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[3], timeAllocationToUse),
    options: {
      enabled: enabled && !!groups[3]?.length,
    },
  });

  const {
    data: defaultDataFifth,
    isFetching: isFetchingDefaultDataFifth,
    isLoading: isLoadingDefaultDataFifth,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[4], timeAllocationToUse),
    options: {
      enabled: enabled && !!groups[4]?.length,
    },
  });

  const {
    data: defaultDataSixth,
    isFetching: isFetchingDefaultDataSixth,
    isLoading: isLoadingDefaultDataSixth,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[5], timeAllocationToUse),
    options: {
      enabled: enabled && !!groups[5]?.length,
    },
  });

  const {
    data: customDataFirst,
    isFetching: isFetchingCustomDataFirst,
    isLoading: isLoadingCustomDataFirst,
  } = useWorkPeriodsMeasureMultiple({
    payload: useCustomPayload(customGroups[0], timeAllocationToUse),
    options: {
      enabled: enabled && !!customGroups[0]?.length,
    },
  });

  const {
    data: customDataSecond,
    isFetching: isFetchingCustomDataSecond,
    isLoading: isLoadingCustomDataSecond,
  } = useWorkPeriodsMeasureMultiple({
    payload: useCustomPayload(customGroups[1], timeAllocationToUse),
    options: {
      enabled: enabled && !!customGroups[1]?.length,
    },
  });

  const {
    data: customDataThird,
    isFetching: isFetchingCustomDataThird,
    isLoading: isLoadingCustomDataThird,
  } = useWorkPeriodsMeasureMultiple({
    payload: useCustomPayload(customGroups[2], timeAllocationToUse),
    options: {
      enabled: enabled && !!customGroups[2]?.length,
    },
  });

  const {
    data: customDataFourth,
    isFetching: isFetchingCustomDataFourth,
    isLoading: isLoadingCustomDataFourth,
  } = useWorkPeriodsMeasureMultiple({
    payload: useCustomPayload(customGroups[3], timeAllocationToUse),
    options: {
      enabled: enabled && !!customGroups[3]?.length,
    },
  });

  const transformerPayload = useTransformerPayload(TRANSFORMER_MEASURES, timeAllocationToUse);
  const {
    data: transformerData,
    isFetching: isFetchingTransformerData,
    isLoading: isLoadingTransformerData,
  } = useWorkPeriodsMeasureMultiple({
    payload: transformerPayload,
    options: { enabled },
  });

  const data = Object.entries(defaultDataFirst || {})
    .concat(Object.entries(defaultDataSecond || {}))
    .concat(Object.entries(defaultDataThird || {}))
    .concat(Object.entries(defaultDataFourth || {}))
    .concat(Object.entries(defaultDataFifth || {}))
    .concat(Object.entries(defaultDataSixth || {}))
    .concat(Object.entries(transformerData || {}))
    .concat(Object.entries(customDataFirst || {}))
    .concat(Object.entries(customDataSecond || {}))
    .concat(Object.entries(customDataThird || {}))
    .concat(Object.entries(customDataFourth || {}))
    .reduce((acc, [key, value]) => {
      acc[key] = { ...acc[key], ...value };
      return acc;
    }, {} as MeasureDataByEntity);

  const isFetching =
    isFetchingDefaultDataFirst ||
    isFetchingDefaultDataSecond ||
    isFetchingDefaultDataThird ||
    isFetchingDefaultDataFourth ||
    isFetchingDefaultDataFifth ||
    isFetchingDefaultDataSixth ||
    isFetchingTransformerData ||
    isFetchingCustomDataFirst ||
    isFetchingCustomDataSecond ||
    isFetchingCustomDataThird ||
    isFetchingCustomDataFourth;
  const isLoading =
    isLoadingDefaultDataFirst ||
    isLoadingDefaultDataSecond ||
    isLoadingDefaultDataThird ||
    isLoadingDefaultDataFourth ||
    isLoadingDefaultDataFifth ||
    isLoadingDefaultDataSixth ||
    isLoadingTransformerData ||
    isLoadingCustomDataFirst ||
    isLoadingCustomDataSecond ||
    isLoadingCustomDataThird ||
    isLoadingCustomDataFourth;

  return { data, isFetching, isLoading };
};

/**
 * Custom hook that returns a payload for fetching work period measures.
 * The payload includes entity type, ids, date range, measure names and time allocation type.
 *
 * @param {Measure[]} measures - Array of measure names to fetch
 * @param {TimeAllocationType} timeAllocation - The time allocation type for the payload
 * @returns {UseWorkPeriodsMeasurePayloadMultiple} The payload for fetching work period measures
 */
const usePayload = (measures: Measure[], timeAllocation: TimeAllocationType): UseWorkPeriodsMeasurePayloadMultiple => {
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTaskType = useProcessAnalysisStore((state) => state.selectedTaskType);
  const { startDate, endDate } = useDateRange();
  const entities = useEntities();

  return {
    entity: getEntityType(activeTab),
    ids: entities,
    start_date: formatToISODate(startDate),
    end_date: formatToISODate(endDate),
    measure_name: measures,
    time_allocation_type: timeAllocation,
    ...(selectedTaskType ? { task_type: selectedTaskType } : {}),
  };
};

/**
 * Custom hook that returns a payload for fetching transformer work period measures.
 * Similar to usePayload but includes the transformer parameter.
 *
 * @param {Measure[]} measures - Array of measure names to fetch
 * @param {TimeAllocationType} timeAllocation - The time allocation type for the payload
 * @returns {UseWorkPeriodsMeasurePayloadMultiple} The payload for fetching transformer work period measures
 */
const useTransformerPayload = (
  measures: Measure[],
  timeAllocation: TimeAllocationType,
): UseWorkPeriodsMeasurePayloadMultiple => {
  const basePayload = usePayload(measures, timeAllocation);
  return {
    ...basePayload,
    transformer: Transformer.TimeAllocationValuesOnly,
  };
};

/**
 * Custom hook that returns a payload for fetching custom work period measures.
 * Similar to usePayload but includes additional parameters for custom measures.
 *
 * @param {string[]} measures - Array of custom measure names to fetch
 * @param {TimeAllocationType} timeAllocation - The time allocation type for the payload
 * @returns {UseWorkPeriodsMeasurePayloadMultiple} The payload for fetching custom work period measures
 */
const useCustomPayload = (
  measures: string[],
  timeAllocation: TimeAllocationType,
): UseWorkPeriodsMeasurePayloadMultiple => {
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTaskType = useProcessAnalysisStore((state) => state.selectedTaskType);
  const { startDate, endDate } = useDateRange();
  const entities = useEntities();
  return {
    entity: getEntityType(activeTab),
    ids: entities,
    start_date: formatToISODate(startDate),
    end_date: formatToISODate(endDate),
    measure_name: measures,
    time_allocation_type: timeAllocation,
    transformer: Transformer.ReturnFirstMetricOnly,
    is_custom: true,
    ...(selectedTaskType ? { task_type: selectedTaskType } : {}),
  };
};

export { useKeyMeasuresOverTimeData };
